import { createRoot } from 'react-dom/client';
import { Provider, useDispatch } from 'react-redux';
import { RouterProvider, createMemoryRouter } from 'react-router';
import { ModelType, type OnlineModelType, VirtualModelType } from '../../common/enums.js';
import { useShoppingBasket } from '../../common/hooks/useShoppingBasket.js';
import { AnalyticsEventOrigins } from '../../common/utils/analyticsUtils';
import {
  SubscriptionCardsByModelTypeWrapper,
  SupportedSubscriptionCardModels,
} from '../../components/OrderSubscriptionSelection/SubscriptionCardsByModelTypeWrapper.js';
import { addToBasket } from '../../components/ShoppingBasket/shoppingBasketFunctions';
import { addToCart, updateCartItem } from '../../selfservice/actions/index.js';
import { getShoppingCart, setShoppingCart } from '../../selfservice/common/localStorageUtils.js';
import { AuthProvider } from './AuthProvider.js';
import { OnlineModelContextProvider } from './onlineModel/onlineModelContextProvider.js';
import type { ShoppingCartItemForCheckout } from '../../common/types/checkout.js';
import type { CartItemContainer } from '../../components/ProductDetails/ProductDetails.js';
import type { AddToShoppingBasketProps } from '../../components/ShoppingBasket/shoppingBasketFunctions';
import type { CartConfig } from '../../selfservice/actions/index.js';
import type { CartItemType } from '../../selfservice/common/shopping-cart/shoppingCartInterfaces.js';
import type { State } from '../../selfservice/common/store.js';
import type { Root } from 'react-dom/client';
import type { Store } from 'redux';

interface ExposedFunctionsProps {
  store: Store<State>;
}

// Track mounted React roots and cached routers for cleanup and performance
const mountedRoots = new Map<string, Root>();
const routerCache = new Map<string, ReturnType<typeof createMemoryRouter>>();

const getMountKey = (divId: string, cardKey: string) => `${divId}::${cardKey}`;
const getContainerId = (divId: string, cardKey: string) => `${divId}-subscription-cards-${encodeURIComponent(cardKey)}`;

// Map model names (e.g. "MobileM2M", "LiikkuvaWifi") to/from values for 3rd party API convenience
const allModelTypes = { ...ModelType, ...VirtualModelType };
const modelNameToType = Object.fromEntries(Object.entries(allModelTypes).map(([name, val]) => [name, val])) as Record<
  string,
  OnlineModelType
>;
const typeToModelName = Object.fromEntries(Object.entries(allModelTypes).map(([name, val]) => [val, name])) as Record<
  OnlineModelType,
  string
>;

const mountSubscriptionCards = (
  divId: string,
  onlineModelType: string,
  store: Store<State>,
  headerFilter?: string[],
  compact?: boolean
) => {
  const targetElement = document.getElementById(divId);
  if (!targetElement) {
    console.warn(`Element with id "${divId}" not found`);
    return;
  }

  const modelType = (modelNameToType[onlineModelType] || onlineModelType) as OnlineModelType;
  if (!SupportedSubscriptionCardModels.includes(modelType)) {
    console.warn(
      `Unsupported online model type: "${onlineModelType}". Supported types:`,
      SupportedSubscriptionCardModels
    );
    return;
  }

  const validHeaderFilter = headerFilter?.filter(s => typeof s === 'string' && s.trim().length > 0);
  const cacheKey = validHeaderFilter?.length ? `${modelType}-${validHeaderFilter.sort().join(',')}` : modelType;
  const fullCacheKey = compact ? `${cacheKey}-compact` : cacheKey;
  const mountKey = getMountKey(divId, fullCacheKey);
  if (mountedRoots.has(mountKey)) {
    return;
  }

  const containerDiv = document.createElement('div');
  containerDiv.id = getContainerId(divId, fullCacheKey);
  targetElement.insertAdjacentElement('afterend', containerDiv);

  let router = routerCache.get(fullCacheKey);
  if (!router) {
    router = createMemoryRouter([
      {
        path: '/',
        element: (
          <AuthProvider>
            <OnlineModelContextProvider initialData={{}}>
              <SubscriptionCardsByModelTypeWrapper
                onlineModelType={modelType}
                headerFilter={validHeaderFilter}
                eventOrigin={AnalyticsEventOrigins.FROSMO}
                compact={compact}
              />
            </OnlineModelContextProvider>
          </AuthProvider>
        ),
      },
    ]);
    routerCache.set(fullCacheKey, router);
  }

  const root = createRoot(containerDiv);
  root.render(
    <Provider store={store}>
      <RouterProvider router={router} />
    </Provider>
  );

  mountedRoots.set(mountKey, root);

  const observer = new MutationObserver(() => {
    if (!document.contains(targetElement) || !document.contains(containerDiv)) {
      observer.disconnect();
      if (mountedRoots.get(mountKey) === root) {
        root.unmount();
        mountedRoots.delete(mountKey);
        if (containerDiv.parentNode) {
          containerDiv.remove();
        }
      }
    }
  });

  if (targetElement.parentNode) {
    observer.observe(targetElement.parentNode, {
      childList: true,
      subtree: true,
    });
  }
};

export const ExposedFunctions: React.FC<ExposedFunctionsProps> = (props: ExposedFunctionsProps) => {
  const { setShoppingBasket, shoppingBasket } = useShoppingBasket();
  const dispatch = useDispatch();
  const store = props.store;
  (
    window as typeof window & {
      app: {
        getStore: () => { getState: () => State };
        shoppingCart: {
          addItemToCart: (
            cartItemContainer: CartItemContainer,
            onAddToCart?: () => void,
            cartConfig?: CartConfig
          ) => void;
          getCart: object;
          updateItemInCart: (
            cartItem: ShoppingCartItemForCheckout,
            newQuantity: number,
            addOnsToBeRemoved: string[],
            onUpdateCart?: () => void
          ) => void;
          setCart: (cart?: CartItemType[]) => void;
        };
        shoppingBasket: {
          setBasket: (basket: string) => void;
          getBasket: object;
          addToBasket: (data: AddToShoppingBasketProps) => void;
        };
        insertSubscriptionCards: (divId: string, onlineModelType: string, headerFilter?: string[]) => void;
        insertCompactSubscriptionCards: (divId: string, onlineModelType: string, headerFilter?: string[]) => void;
        unmountSubscriptionCards: (divId: string) => void;
        getSupportedSubscriptionCards: () => { name: string; guid: string }[];
      };
    }
  ).app = {
    getStore: () => {
      return {
        getState: () => {
          const { ...restState } = store.getState();
          return restState;
        },
      };
    },
    shoppingCart: {
      addItemToCart: (cartItemContainer: CartItemContainer, onAddToCart?: () => void, cartConfig?: CartConfig) => {
        dispatch(addToCart(cartItemContainer, onAddToCart, cartConfig));
      },
      getCart: () => {
        return JSON.parse(getShoppingCart());
      },
      updateItemInCart: (
        cartItem: ShoppingCartItemForCheckout,
        newQuantity: number,
        addOnsToBeRemoved: string[],
        onUpdateCart?: () => void
      ) => {
        dispatch(updateCartItem(cartItem, newQuantity, addOnsToBeRemoved, onUpdateCart));
      },
      setCart: (cart?: CartItemType[]) => {
        setShoppingCart(JSON.stringify(cart as CartItemType[]));
      },
    },
    shoppingBasket: {
      setBasket(basket: string) {
        setShoppingBasket(basket);
      },
      getBasket: () => {
        return shoppingBasket;
      },
      addToBasket: (data: AddToShoppingBasketProps) => {
        addToBasket(data);
      },
    },
    insertSubscriptionCards: (divId: string, onlineModelType: string, headerFilter?: string[]) => {
      mountSubscriptionCards(divId, onlineModelType, store, headerFilter);
    },
    insertCompactSubscriptionCards: (divId: string, onlineModelType: string, headerFilter?: string[]) => {
      mountSubscriptionCards(divId, onlineModelType, store, headerFilter, true);
    },
    getSupportedSubscriptionCards: () =>
      SupportedSubscriptionCardModels.map(guid => ({
        name: typeToModelName[guid] || 'Unknown',
        guid,
      })),
    unmountSubscriptionCards: (divId: string) => {
      const mountPrefix = `${divId}::`;
      for (const [mountKey, root] of mountedRoots) {
        if (mountKey.startsWith(mountPrefix)) {
          root.unmount();
          mountedRoots.delete(mountKey);
        }
      }
      for (const container of document.querySelectorAll(`[id^="${divId}-subscription-cards-"]`)) {
        container.remove();
      }
    },
  };
  return null;
};
